import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { StoreFinderComponentDef } from '@df/catalog-ui/store/finder/store-finder.component.def';
import { IStoreDataV3 } from '@df/catalog/store/store';
import { StoreResource } from '@df/catalog/store/store.resource';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { ToggleService } from '@df/ui/toggle/toggle.service';
import { Nexus } from '@tomandco/nexus-api-types';
import sortBy from 'lodash-es/sortBy';
import uniq from 'lodash-es/uniq';
import { Product } from '../../catalog/product/product.decorator';
import { LocalStore } from '../../catalog/store/store.decorator';
import { LocationPostcodeService } from '../../common/delivery/location-postcode.service';
import { BranchService, TProductState } from '../branch.service';
import { BranchCollectionComponent } from '../collection/branch-collection.component';

function getCountriesSorted(app: App): Nexus.Lib.General.Country[] {
    const allCountries = app.apiConfigService.countries;
    const validCountryIds = uniq(app.resources.store.getAllExisting().map(i => i.data?.country_id));
    const storeCountry = app.multisite.current.country;
    const storeCountries = app.multisite.current.countries;
    return sortBy(
        // only matching countries
        allCountries?.filter(i => validCountryIds.includes(i.code)),
        [
            i => i.name,
            i => {
                if (storeCountry === i.code) {
                    return 10;
                }
                if (storeCountries?.includes(i.code)) {
                    return 5;
                }
                return 1;
            }
        ]
    );
}

@Component({
    selector: 'branch-finder',
    templateUrl: 'branch-finder.html',
    styleUrls: ['branch-finder.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BranchFinderComponent extends StoreFinderComponentDef<LocalStore> implements OnInit {
    @Input()
    product?: Product;

    @Output()
    onBranchChange = new EventEmitter<LocalStore<IStoreDataV3>>();

    /**
     * List of countries you may select location from
     */
    countries: Nexus.Lib.General.Country[] = [];

    /**
     * Currently previewed store
     */
    selectedBranch?: LocalStore<IStoreDataV3>;

    /**
     * If store preview is visible
     */
    isPreviewVisible = false;

    /**
     * Stores verbosity
     */
    protected override loacateVerbosity = 3;
    protected locationStatus = new Status();
    protected confirmStatus = new Status();

    private app = inject(App);
    protected toggleService = inject(ToggleService);
    private storeResource = inject(StoreResource);
    private branchService = inject(BranchService);
    private locationPostcodeService = inject(LocationPostcodeService);
    private modalRef = inject(ModalRef<BranchCollectionComponent>);

    /**
     * Buffer with stock statuses for all stores for given product
     */
    private stockStatusMap: TProductState | undefined;

    /**
     * Init
     */
    override ngOnInit(): void {
        this.initCountries();
        this.loadStockStatus();
        this.loadCurrentLocation();
        this.address = this.hashStorageService.get('location');
        if (this.address) {
            this.search();
        } else {
            this.loadAndShowAll();
        }
        // NOTE: we do NOT want init from the parent here
    }

    /**
     * When country changes update all models as there is country filter on it
     */
    onCountryChange() {
        this.updateAllFindableModels();
    }

    /**
     * Cancel search results
     */
    override reset(): void {
        this.address = '';
        this.lastSearchHash = '';
        this._isUserSearch = false;
        this.locationState = undefined;
        this.loadAndShowAll();
    }

    /**
     * If results are visible
     */
    get isResultsView() {
        return this.isUserSearch;
    }

    /**
     * Prepares list of countries with stores
     */
    private initCountries() {
        this.countries = getCountriesSorted(this.app);
        const validCountryIds = this.storeResource.getAllExisting().map(i => (i?.data as IStoreDataV3)?.country_id);
        const preferedCountries = [this.hashStorageService.get('country'), this.app.multisite.current.country, validCountryIds[0]];
        this.country = preferedCountries.find(i => validCountryIds.includes(i));

        this.onCountryChange();
    }

    override set locationState(locationState: string | undefined) {
        return;
    }

    protected selectBranch(store: LocalStore<IStoreDataV3>) {
        if (!this.isDisabled(store)) {
            this.selectedBranch = store;
            this.onBranchChange.emit(store);
            this.detectChanges();
        }
    }

    protected async loadCurrentLocation() {
        await this.locationPostcodeService.loadLocation(this.locationStatus, postcode => {
            this.address = postcode;
            this.detectChanges();
            if (this.address) {
                this.search();
            }
        });
    }

    protected getQty(store: LocalStore<IStoreDataV3>): number | undefined {
        return this.stockStatusMap ? this.stockStatusMap[store.data!.branch_number!] || 0 : undefined;
    }

    protected isDisabled(store: LocalStore<IStoreDataV3>): boolean {
        // return !!this.stockStatusMap && (this.getQty(store) || 0) < 1;
        return false;
    }

    override onFindableModelsUpdate() {
        let models = this.findableModels?.filter(i => !!this.branchService.toBranchId(i));

        // The base search() drops stores whose latitude/longitude are falsy
        // (e.g. PGR+, stored as 0,0). Append them at the end of the
        // distance-sorted list so they remain selectable after a postcode search.
        if (this.isUserSearch && models) {
            const presentIds = new Set(models.map(m => m.id));
            const missingCoordBranches = this.allFindableModels.filter(
                m => !!this.branchService.toBranchId(m) && !presentIds.has(m.id) && !(m.data?.latitude && m.data?.longitude)
            );
            models = [...models, ...missingCoordBranches];
        }

        this.findableModels = models;
        super.onFindableModelsUpdate();
    }

    /**
     * Loads status for requested product (if exists)
     */
    private async loadStockStatus() {
        const sku = this.product?.data?.sku;
        if (sku) {
            this.stockStatusMap = await this.branchService.retrieveInAll(sku);
            this.detectChanges();
        }
    }

    protected confirm() {
        if (!this.selectedBranch?.data?.branch_number) {
            return;
        }
        this.branchService.updateBranch(
            this.selectedBranch?.data?.branch_number,
            () => {
                this.modalRef.close();
            },
            undefined,
            this.confirmStatus
        );
    }
}
